import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, set } from '@ember/object';
import { ajax } from 'discourse/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { debounce } from '@ember/runloop';

export default class AdminPluginsProxyviewsDirectoryController extends Controller {
  @service dialog;
  @tracked activeTab = 'overview';
  @tracked savingId = null;
  @tracked message = null;

  // Collapsible calculator editing — one pricing row editable at a time.
  // Edits go into calcDraft (not the live model) so the two-way <Input>
  // binding can't silently corrupt data on navigation.
  @tracked editingCalcId = null;
  @tracked calcDraft = {};
  @tracked searchQuery = '';
  @tracked rejectReasons = {};
  @tracked providersLoading = false;

  @tracked providers = [];
  @tracked providersMeta = {};
  @tracked providersPage = 1;
  @tracked stats = {};
  @tracked claims = [];
  @tracked pricingTiers = [];
  @tracked regions = [];
  @tracked settings = [];
  @tracked providerPricings = [];
  @tracked allProviders = [];
  @tracked overview = {};
  @tracked backupSummary = {};
  @tracked siteSettings = [];

  // ── Price History ──────────────────────────────────────────────────────
  @tracked priceHistory = [];
  @tracked priceHistoryLoading = false;
  @tracked expandedPriceHistoryId = null;

  // ── CSV Import ─────────────────────────────────────────────────────────
  @tracked showImportForm = false;
  @tracked importPreview = null;
  @tracked importErrors = [];
  @tracked importToken = null;

  // ── Featured Purchases ─────────────────────────────────────────────────
  @tracked featuredPurchases = [];
  @tracked featuredPurchasesMeta = {};
  @tracked featuredPurchasesPage = 1;
  @tracked featuredPurchasesLoading = false;

  // ── Edit Proposals ─────────────────────────────────────────────────────
  @tracked editProposals = [];
  @tracked editProposalsLoading = false;

  // Human-readable labels for the section keys ProxyViews::SystemBackup.summary
  // returns — kept in one place here rather than duplicated per-row in the
  // template.
  BACKUP_SECTION_LABELS = {
    providers: 'Providers',
    provider_claims: 'Provider Claims',
    calculator_settings: 'Calculator Settings',
    calculator_pricing_tiers: 'Calculator Pricing Tiers',
    calculator_region_multipliers: 'Calculator Region Multipliers',
    calculator_provider_pricing: 'Calculator Provider Overrides',
    guides: 'SEO Guides',
    guide_custom_categories: 'Custom Guide Categories',
    comparisons: 'SEO Comparisons',
    best_for_use_cases: 'SEO Best For Use Cases',
    alternative_metadata: 'SEO Alternatives Overrides',
    seo_robots_config: 'Robots.txt Config',
    benchmark_runs: 'Benchmark Runs',
    analytics_funnels: 'Analytics Funnels',
    analytics_experiments: 'A/B Experiments',
    provider_edit_proposals: 'Provider Edit Proposals',
    governance_proposals: 'Governance Proposals',
    governance_comparison_requests: 'Governance Comparison Requests',
    reviews: 'Reviews',
    review_responses: 'Review Responses',
    curated_videos: 'Curated Videos',
  };

  // A count of `null` means the owning plugin isn't enabled/installed here
  // (see lib/proxyviews/system_backup.rb) — distinguished from `0`, which
  // means the plugin is present but that table is empty.
  get backupSummaryRows() {
    return Object.keys(this.BACKUP_SECTION_LABELS).map((key) => {
      const count = this.backupSummary[key];
      return {
        key,
        label: this.BACKUP_SECTION_LABELS[key],
        count,
        available: count !== null && count !== undefined,
      };
    });
  }

  // Curation search now hits the backend (q + page) instead of filtering
  // whatever page happens to already be loaded — the dashboard endpoint
  // paginates 50 providers at a time, so a client-only filter could never
  // find (or let you curate) anything past the first 50.
  get filteredProviders() {
    return this.providers;
  }

  get providersTotalPages() {
    return this.providersMeta.total_pages || 1;
  }

  get providersHasPrevPage() {
    return this.providersPage > 1;
  }

  get providersHasNextPage() {
    return this.providersPage < this.providersTotalPages;
  }

  // The overview endpoint returns null (not 0) for a count when the owning
  // plugin is disabled, so we can tell "nothing pending" apart from
  // "that plugin isn't installed here" — these getters do that check in JS
  // rather than relying on a bare `null` literal inside a Handlebars
  // subexpression, which is more predictable across templating engines.
  get reviewsAvailable() {
    return this.overview.reviews_pending !== null && this.overview.reviews_pending !== undefined;
  }

  get governanceAvailable() {
    return (
      this.overview.governance_proposals_pending !== null &&
      this.overview.governance_proposals_pending !== undefined
    );
  }

  async loadProviders() {
    this.providersLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-directory/dashboard', {
        data: { q: this.searchQuery, page: this.providersPage },
      });
      this.providers = data.providers || [];
      this.providersMeta = data.meta || {};
      this.stats = data.stats || this.stats;
    } catch (err) {
      this.message = { text: 'Failed to load providers.', type: 'danger' };
    } finally {
      this.providersLoading = false;
    }
  }

  @action
  updateProviderSearch(e) {
    this.searchQuery = e.target.value;
    this.providersPage = 1;
    debounce(this, this.loadProviders, 250);
  }

  @action
  nextProvidersPage() {
    if (!this.providersHasNextPage || this.providersLoading) {
      return;
    }
    this.providersPage += 1;
    this.loadProviders();
    this.scrollToTableTop();
  }

  @action
  prevProvidersPage() {
    if (!this.providersHasPrevPage || this.providersLoading) {
      return;
    }
    this.providersPage -= 1;
    this.loadProviders();
    this.scrollToTableTop();
  }

  // Scroll the admin content area back to the table top so the user sees
  // fresh results instead of staring at the bottom of the previous page.
  scrollToTableTop() {
    const table = document.querySelector('.pd-admin-table-container');
    if (table) {
      table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // ── Provider Change History ────────────────────────────────────────────
  // ProviderEvent logs every field change (job: record_provider_events).
  // This lazy-loads the history for one provider into an expandable row,
  // same pattern as the content editor.
  @tracked expandedHistoryId = null;
  @tracked historyLoading = false;
  @tracked historyEvents = [];

  @action
  async toggleHistory(provider) {
    if (this.expandedHistoryId === provider.id) {
      this.expandedHistoryId = null;
      this.historyEvents = [];
      return;
    }
    this.expandedHistoryId = provider.id;
    this.historyLoading = true;
    this.historyEvents = [];
    try {
      const data = await ajax(`/admin/plugins/proxyviews-directory/networks/${provider.id}/events`);
      this.historyEvents = data.events || [];
    } catch (err) {
      this.message = { text: 'Failed to load history.', type: 'danger' };
      this.expandedHistoryId = null;
    } finally {
      this.historyLoading = false;
    }
  }

  // ── Price History ──────────────────────────────────────────────────────
  @action
  async togglePriceHistory(provider) {
    if (this.expandedPriceHistoryId === provider.id) {
      this.expandedPriceHistoryId = null;
      this.priceHistory = [];
      return;
    }
    this.expandedPriceHistoryId = provider.id;
    this.priceHistoryLoading = true;
    this.priceHistory = [];
    try {
      const data = await ajax(`/admin/plugins/proxyviews-directory/price-history/${provider.id}`);
      this.priceHistory = data.history || [];
    } catch (err) {
      this.message = { text: 'Failed to load price history.', type: 'danger' };
      this.expandedPriceHistoryId = null;
    } finally {
      this.priceHistoryLoading = false;
    }
  }

  // ── CSV Import ─────────────────────────────────────────────────────────
  @action
  toggleImportForm() {
    this.showImportForm = !this.showImportForm;
    this.importPreview = null;
    this.importErrors = [];
    this.message = null;
  }

  @action
  async handleImportUpload(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    this.savingId = 'import-preview';
    this.message = null;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await ajax('/admin/plugins/proxyviews-directory/import.csv', {
        type: 'POST',
        data: formData,
        contentType: false,
        processData: false,
      });
      this.importPreview = result.preview || result.rows || [];
      this.importErrors = result.errors || [];
      this.importToken = result.token || null;
    } catch (err) {
      this.importErrors = err.jqXHR?.responseJSON?.errors ||
        err.responseJSON?.errors ||
        ['Failed to parse CSV.'];
      this.importPreview = null;
    } finally {
      this.savingId = null;
      event.target.value = '';
    }
  }

  @action
  async confirmImport() {
    this.savingId = 'import-confirm';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-directory/import.csv', {
        type: 'POST',
        data: { confirm: 'true', token: this.importToken },
      });
      this.message = { text: `Import complete: ${result.created || 0} created, ${result.updated || 0} updated.`, type: 'success' };
      this.clearMessageAfterDelay();
      this.importPreview = null;
      this.importErrors = [];
      this.showImportForm = false;
      this.providersPage = 1;
      await this.loadProviders();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to confirm import.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  // ── Featured Purchases ─────────────────────────────────────────────────
  @action
  async loadFeaturedPurchases() {
    this.featuredPurchasesLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-directory/featured-purchases', {
        data: { page: this.featuredPurchasesPage },
      });
      this.featuredPurchases = data.featured_purchases || data.purchases || [];
      this.featuredPurchasesMeta = data.meta || {};
    } catch (err) {
      this.message = { text: 'Failed to load featured purchases.', type: 'danger' };
    } finally {
      this.featuredPurchasesLoading = false;
    }
  }

  @action
  async extendPurchase(purchase) {
    this.savingId = 'extend-' + purchase.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/featured-purchases/${purchase.id}`, {
        type: 'PUT',
        data: { extend_days: 30 },
      });
      this.message = { text: 'Extended purchase by 30 days.', type: 'success' };
      this.clearMessageAfterDelay();
      await this.loadFeaturedPurchases();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to extend purchase.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async cleanupFeatured() {
    this.savingId = 'cleanup-featured';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-directory/featured-purchases/cleanup', {
        type: 'POST',
      });
      this.message = { text: result.message || 'Cleanup complete.', type: 'success' };
      this.clearMessageAfterDelay();
      await this.loadFeaturedPurchases();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to cleanup expired purchases.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  // ── Edit Proposals ─────────────────────────────────────────────────────
  @action
  async loadEditProposals() {
    this.editProposalsLoading = true;
    try {
      const data = await ajax('/admin/plugins/proxyviews-directory/edit-proposals');
      this.editProposals = data.edit_proposals || data.proposals || [];
    } catch (err) {
      this.message = { text: 'Failed to load edit proposals.', type: 'danger' };
    } finally {
      this.editProposalsLoading = false;
    }
  }

  @action
  async approveProposal(proposal) {
    this.savingId = 'approve-proposal-' + proposal.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/edit-proposals/${proposal.id}/approve`, {
        type: 'POST',
      });
      this.message = { text: 'Proposal approved.', type: 'success' };
      this.clearMessageAfterDelay();
      this.editProposals = this.editProposals.filter((p) => p.id !== proposal.id);
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to approve proposal.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async rejectProposal(proposal) {
    this.savingId = 'reject-proposal-' + proposal.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/edit-proposals/${proposal.id}/reject`, {
        type: 'POST',
      });
      this.message = { text: 'Proposal rejected.', type: 'success' };
      this.clearMessageAfterDelay();
      this.editProposals = this.editProposals.filter((p) => p.id !== proposal.id);
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to reject proposal.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  changeTab(tab) {
    this.activeTab = tab;
    this.message = null;
    if (tab === 'featured') {
      this.loadFeaturedPurchases();
    } else if (tab === 'edits') {
      this.loadEditProposals();
    }
  }

  // ── Provider Content Editor ────────────────────────────────────────────
  // The curation table edits metadata (price/status/featured). This expandable
  // panel lazy-loads the heavy editorial fields (description, profile, pros/
  // cons, FAQ) per provider so the paginated list stays light. Only one
  // provider's content panel is open at a time.
  @tracked expandedContentId = null;
  @tracked contentLoading = false;
  @tracked contentDraft = null;

  @action
  async toggleContentEditor(provider) {
    if (this.expandedContentId === provider.id) {
      this.expandedContentId = null;
      this.contentDraft = null;
      return;
    }
    this.expandedContentId = provider.id;
    this.contentLoading = true;
    this.contentDraft = null;
    try {
      const data = await ajax(`/admin/plugins/proxyviews-directory/content/${provider.id}`);
      // Flatten pros/cons/faq arrays into newline strings for the textarea,
      // and protocols/countries/use_cases into comma strings for easy editing.
      this.contentDraft = {
        description: data.content.description || '',
        logo_url: data.content.logo_url || '',
        supported_protocols: (data.content.supported_protocols || []).join(', '),
        country_codes: (data.content.country_codes || []).join(', '),
        use_cases: (data.content.use_cases || []).join(', '),
        pricing_tiers: JSON.stringify(data.content.pricing_tiers || {}, null, 2),
        ip_pool_size: data.content.ip_pool_size || '',
        asn_diversity_score: data.content.asn_diversity_score || '',
        uptime_percentage: data.content.uptime_percentage || '',
        benchmark_score: data.content.benchmark_score || '',
        verified_block_rate: data.content.verified_block_rate || '',
        avg_session_affinity_length: data.content.avg_session_affinity_length || '',
        verification_tier: data.content.verification_tier || 'none',
        overview: data.profile.overview || '',
        what_works: data.profile.what_works || '',
        what_hurts: data.profile.what_hurts || '',
        setup_guide: data.profile.setup_guide || '',
        pros: (data.profile.pros || []).join('\n'),
        cons: (data.profile.cons || []).join('\n'),
        faq: JSON.stringify(data.profile.faq || [], null, 2),
        sidebar_cta_text: data.profile.sidebar_cta_text || '',
        affiliate_url: data.profile.affiliate_url || '',
        deal: data.content.metadata?.deal || '',
        coupon_code: data.content.metadata?.coupon_code || '',
        expiry: data.content.metadata?.expiry || '',
        free_trial: data.content.metadata?.free_trial || '',
        speed: data.content.metadata?.speed || '',
        locations: data.content.metadata?.locations || '',
        pay_as_you_go: data.content.metadata?.pay_as_you_go || '',
      };
    } catch (err) {
      this.message = { text: 'Failed to load content.', type: 'danger' };
      this.expandedContentId = null;
    } finally {
      this.contentLoading = false;
    }
  }

  @action
  updateContentField(field, event) {
    this.contentDraft = { ...this.contentDraft, [field]: event.target.value };
  }

  @action
  async saveProviderContent(provider) {
    this.savingId = 'content-' + provider.id;
    this.message = null;
    const d = this.contentDraft;
    try {
      // Parse the JSON/comma fields back into their structured shapes.
      let pricingTiers = {};
      try { pricingTiers = JSON.parse(d.pricing_tiers || '{}'); } catch (e) { /* leave empty */ }
      let faq = [];
      try { faq = JSON.parse(d.faq || '[]'); } catch (e) { /* leave empty */ }

      await ajax(`/admin/plugins/proxyviews-directory/content/${provider.id}`, {
        type: 'PUT',
        data: {
          description: d.description || undefined,
          logo_url: d.logo_url || undefined,
          ip_pool_size: d.ip_pool_size || undefined,
          asn_diversity_score: d.asn_diversity_score || undefined,
          uptime_percentage: d.uptime_percentage || undefined,
          benchmark_score: d.benchmark_score || undefined,
          verified_block_rate: d.verified_block_rate || undefined,
          avg_session_affinity_length: d.avg_session_affinity_length || undefined,
          verification_tier: d.verification_tier || undefined,
          supported_protocols: d.supported_protocols.split(',').map(s => s.trim()).filter(Boolean),
          country_codes: d.country_codes.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
          use_cases: d.use_cases.split(',').map(s => s.trim()).filter(Boolean),
          pricing_tiers: pricingTiers,
          metadata: {
            deal: d.deal || undefined,
            coupon_code: d.coupon_code || undefined,
            expiry: d.expiry || undefined,
            free_trial: d.free_trial || undefined,
            speed: d.speed || undefined,
            locations: d.locations || undefined,
            pay_as_you_go: d.pay_as_you_go || undefined
          },
          profile_attributes: {
            overview: d.overview || undefined,
            what_works: d.what_works || undefined,
            what_hurts: d.what_hurts || undefined,
            setup_guide: d.setup_guide || undefined,
            sidebar_cta_text: d.sidebar_cta_text || undefined,
            affiliate_url: d.affiliate_url || undefined,
            pros: d.pros.split('\n').map(s => s.trim()).filter(Boolean),
            cons: d.cons.split('\n').map(s => s.trim()).filter(Boolean),
            faq: faq,
          },
        },
      });
      this.message = { text: `Saved content for ${provider.name}.`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to save content.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  // Plain <select>/<input type="date"> elements don't get Ember <Input>'s
  // built-in two-way binding, so this mutates the row's plain-object field
  // directly. Rows are POJOs inside a @tracked array, and Save reads straight
  // off that object.
  @action
  updateProviderField(provider, field, event) {
    provider[field] = event.target.value;
  }

  // <Input @type="checkbox" @checked={{...}} /> is ONE-WAY in Ember Octane —
  // it renders the value but does NOT write back on toggle. Without this
  // handler the checkbox visually flips but the row's field keeps its old
  // value, so Save silently drops the change. This reads event.target.checked
  // (boolean) rather than .value (string), and reassigns the tracked array so
  // the one-way binding re-renders from the now-correct source.
  @action
  toggleProviderField(provider, field, event) {
    provider[field] = event.target.checked;
    this.providers = [...this.providers];
  }

  // Same one-way fix for the calculator tables (pricing tiers, regions,
  // provider-pricing overrides), whose `active` toggles had the same bug.
  @action
  togglePricingTierActive(tier, event) {
    tier.active = event.target.checked;
    this.pricingTiers = [...this.pricingTiers];
  }

  // ── Collapsible calculator editing helpers ───────────────────────────
  // All 3 calculator tables (tiers, regions, provider-pricing) use the
  // same pattern: click Edit → snapshot into calcDraft → edit in the
  // collapsible panel → Save commits the draft.
  @action
  startEditTier(tier) {
    this.calcDraft = { tier_label: tier.tier_label, min_bandwidth_gb: tier.min_bandwidth_gb, max_bandwidth_gb: tier.max_bandwidth_gb, price_per_gb: tier.price_per_gb };
    this.editingCalcId = tier.id;
  }

  @action
  startEditRegion(region) {
    this.calcDraft = { multiplier: region.multiplier };
    this.editingCalcId = region.id;
  }

  @action
  startEditProviderPricing(pp) {
    this.calcDraft = { proxy_type: pp.proxy_type, price_per_gb: pp.price_per_gb, region_multiplier: pp.region_multiplier };
    this.editingCalcId = pp.id;
  }

  @action
  cancelEditCalc() {
    this.editingCalcId = null;
    this.calcDraft = {};
  }

  @action
  updateCalcDraft(field, event) {
    const val = event.target.type === 'number' ? Number(event.target.value) : event.target.value;
    this.calcDraft = { ...this.calcDraft, [field]: val };
  }

  @action
  toggleRegionActive(region, event) {
    region.active = event.target.checked;
    this.regions = [...this.regions];
  }

  @action
  toggleProviderPricingActive(pricing, event) {
    pricing.active = event.target.checked;
    this.providerPricings = [...this.providerPricings];
  }

  @action
  async saveProvider(provider) {
    this.savingId = 'provider-' + provider.id;
    this.message = null;
    try {
      // `curate` now accepts the full editable field set (widened from just
      // active_vendor/provider_of_month/featured_weight) so a single row
      // save covers everything shown in the table.
      await ajax(`/admin/plugins/proxyviews-directory/curate/${provider.id}`, {
        type: 'PUT',
        data: {
          active_vendor: provider.active_vendor,
          provider_of_month: provider.provider_of_month,
          featured_weight: parseInt(provider.featured_weight) || 0,
          featured_rank: parseInt(provider.featured_rank) || 0,
          featured_until: provider.featured_until || null,
          proxy_type: provider.proxy_type,
          status: provider.status,
          verified: provider.verified,
          website_url: provider.website_url,
          real_cost_per_gb: provider.real_cost_per_gb,
        },
      });
      this.message = { text: `Saved ${provider.name} successfully!`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to save changes. Please try again.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  // ── New Provider ──────────────────────────────────────────────────────

  @tracked showNewProviderForm = false;
  @tracked newProviderName = '';
  @tracked newProviderWebsiteUrl = '';
  @tracked newProviderProxyType = 'residential';
  @tracked newProviderStatus = 'pending';
  @tracked newProviderVerified = false;

  proxyTypeOptions = ['residential', 'datacenter', 'mobile', 'isp', 'rotating'];
  statusOptions = ['pending', 'active', 'suspended', 'archived'];
  verificationTiers = ['none', 'basic', 'benchmark', 'premium'];
  // Matches CalculatorPricingTier::PROXY_TYPES (now includes 'rotating').
  calculatorProxyTypeOptions = ['residential', 'datacenter', 'mobile', 'isp', 'rotating'];

  @action
  toggleNewProviderForm() {
    this.showNewProviderForm = !this.showNewProviderForm;
    this.message = null;
  }

  @action
  updateNewProviderName(e) {
    this.newProviderName = e.target.value;
  }

  @action
  updateNewProviderWebsiteUrl(e) {
    this.newProviderWebsiteUrl = e.target.value;
  }

  @action
  updateNewProviderProxyType(e) {
    this.newProviderProxyType = e.target.value;
  }

  @action
  updateNewProviderStatus(e) {
    this.newProviderStatus = e.target.value;
  }

  @action
  toggleNewProviderVerified() {
    this.newProviderVerified = !this.newProviderVerified;
  }

  @action
  async createProvider() {
    if (!this.newProviderName.trim()) {
      this.message = { text: 'Provider name is required.', type: 'danger' };
      return;
    }

    this.savingId = 'new-provider';
    this.message = null;
    try {
      await ajax('/admin/plugins/proxyviews-directory/networks', {
        type: 'POST',
        data: {
          provider: {
            name: this.newProviderName.trim(),
            website_url: this.newProviderWebsiteUrl.trim() || undefined,
            proxy_type: this.newProviderProxyType,
            status: this.newProviderStatus,
            verified: this.newProviderVerified,
          },
        },
      });
      this.message = { text: `Created ${this.newProviderName}.`, type: 'success' };
      this.clearMessageAfterDelay();
      this.newProviderName = '';
      this.newProviderWebsiteUrl = '';
      this.newProviderProxyType = 'residential';
      this.newProviderStatus = 'pending';
      this.newProviderVerified = false;
      this.showNewProviderForm = false;
      this.providersPage = 1;
      await this.loadProviders();
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') || 'Failed to create provider.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deleteProvider(provider) {
    this.dialog.yesNoConfirm({
      message: `Delete ${provider.name}? This cannot be undone.`,
      didConfirm: async () => {
      
          this.savingId = 'delete-' + provider.id;
          this.message = null;
          try {
            await ajax(`/admin/plugins/proxyviews-directory/networks/${provider.id}`, { type: 'DELETE' });
            this.message = { text: `Deleted ${provider.name}.`, type: 'success' };
            this.clearMessageAfterDelay();
            await this.loadProviders();
          } catch (err) {
            this.message = { text: 'Failed to delete provider.', type: 'danger' };
          } finally {
            this.savingId = null;
          }
        }
    });
  }

  @action
  async approveClaim(claim) {
    this.savingId = 'claim-approve-' + claim.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/claims/${claim.id}/approve`, {
        type: 'POST',
      });
      this.message = { text: `Approved claim for ${claim.provider.name}!`, type: 'success' };
      // Filter out approved claim
      this.claims = this.claims.filter(c => c.id !== claim.id);
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to approve claim.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async rejectClaim(claim) {
    const reason = this.rejectReasons[claim.id] || '';
    if (!reason.trim()) {
      this.message = { text: 'Please enter a rejection reason.', type: 'danger' };
      return;
    }
    this.savingId = 'claim-reject-' + claim.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/claims/${claim.id}/reject`, {
        type: 'POST',
        data: { reason }
      });
      this.message = { text: `Rejected claim for ${claim.provider.name}.`, type: 'success' };
      // Filter out rejected claim
      this.claims = this.claims.filter(c => c.id !== claim.id);
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: 'Failed to reject claim.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async savePricingTier(tier) {
    this.savingId = 'pricing-' + tier.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/calculator/pricing/${tier.id}`, {
        type: 'PUT',
        data: {
          pricing_tier: {
            tier_label: this.calcDraft.tier_label,
            min_bandwidth_gb: parseFloat(this.calcDraft.min_bandwidth_gb),
            max_bandwidth_gb: parseFloat(this.calcDraft.max_bandwidth_gb),
            price_per_gb: parseFloat(this.calcDraft.price_per_gb),
            connections_included: parseFloat(tier.connections_included),
            description: tier.description,
            active: tier.active
          }
        }
      });
      this.pricingTiers = this.pricingTiers.map((t) => (t.id === tier.id ? { ...t, ...this.calcDraft } : t));
      this.editingCalcId = null;
      this.calcDraft = {};
      this.message = { text: `Saved tier "${tier.tier_label}" successfully!`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save tier.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deletePricingTier(tier) {
    this.dialog.yesNoConfirm({
      message: `Delete pricing tier "${tier.tier_label}"? This cannot be undone.`,
      didConfirm: async () => {
          this.savingId = 'delete-pricing-' + tier.id;
          this.message = null;
          try {
            await ajax(`/admin/plugins/proxyviews-directory/calculator/pricing/${tier.id}`, { type: 'DELETE' });
            this.pricingTiers = this.pricingTiers.filter((t) => t.id !== tier.id);
            this.message = { text: `Deleted tier "${tier.tier_label}".`, type: 'success' };
            this.clearMessageAfterDelay();
          } catch (err) {
            this.message = { text: 'Failed to delete tier.', type: 'danger' };
          } finally {
            this.savingId = null;
          }
      }
    });
  }

  @tracked showNewTierForm = false;
  @tracked newTier = { proxy_type: 'residential', tier_label: '', min_bandwidth_gb: 0, max_bandwidth_gb: 100, price_per_gb: 0, connections_included: 0 };

  @action
  toggleNewTierForm() {
    this.showNewTierForm = !this.showNewTierForm;
  }

  @action
  updateNewTierField(field, event) {
    this.newTier = { ...this.newTier, [field]: event.target.value };
  }

  @action
  async createPricingTier() {
    if (!this.newTier.tier_label.trim()) {
      this.message = { text: 'Tier label is required.', type: 'danger' };
      return;
    }
    this.savingId = 'new-tier';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-directory/calculator/pricing', {
        type: 'POST',
        data: {
          pricing_tier: {
            proxy_type: this.newTier.proxy_type,
            tier_label: this.newTier.tier_label,
            min_bandwidth_gb: parseFloat(this.newTier.min_bandwidth_gb) || 0,
            max_bandwidth_gb: parseFloat(this.newTier.max_bandwidth_gb) || 0,
            price_per_gb: parseFloat(this.newTier.price_per_gb) || 0,
            connections_included: parseFloat(this.newTier.connections_included) || 0,
            active: true,
          },
        },
      });
      this.pricingTiers = [...this.pricingTiers, result.pricing_tier];
      this.newTier = { proxy_type: 'residential', tier_label: '', min_bandwidth_gb: 0, max_bandwidth_gb: 100, price_per_gb: 0, connections_included: 0 };
      this.showNewTierForm = false;
      this.message = { text: 'Pricing tier created.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to create tier.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async saveRegionMultiplier(region) {
    this.savingId = 'region-' + region.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/calculator/regions/${region.id}`, {
        type: 'PUT',
        data: {
          region: {
            region_name: region.region_name,
            multiplier: parseFloat(this.calcDraft.multiplier),
            active: region.active
          }
        }
      });
      this.regions = this.regions.map((r) => (r.id === region.id ? { ...r, ...this.calcDraft } : r));
      this.editingCalcId = null;
      this.calcDraft = {};
      this.message = { text: `Saved region "${region.region_name}" successfully!`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save region.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deleteRegion(region) {
    this.dialog.yesNoConfirm({
      message: `Delete region "${region.region_name}"? This cannot be undone.`,
      didConfirm: async () => {
          this.savingId = 'delete-region-' + region.id;
          this.message = null;
          try {
            await ajax(`/admin/plugins/proxyviews-directory/calculator/regions/${region.id}`, { type: 'DELETE' });
            this.regions = this.regions.filter((r) => r.id !== region.id);
            this.message = { text: `Deleted region "${region.region_name}".`, type: 'success' };
            this.clearMessageAfterDelay();
          } catch (err) {
            this.message = { text: 'Failed to delete region.', type: 'danger' };
          } finally {
            this.savingId = null;
          }
      }
    });
  }

  @tracked showNewRegionForm = false;
  @tracked newRegion = { region_name: '', region_code: '', proxy_type: 'residential', multiplier: 1 };

  @action
  toggleNewRegionForm() {
    this.showNewRegionForm = !this.showNewRegionForm;
  }

  @action
  updateNewRegionField(field, event) {
    this.newRegion = { ...this.newRegion, [field]: event.target.value };
  }

  @action
  async createRegion() {
    if (!this.newRegion.region_name.trim()) {
      this.message = { text: 'Region name is required.', type: 'danger' };
      return;
    }
    this.savingId = 'new-region';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-directory/calculator/regions', {
        type: 'POST',
        data: {
          region: {
            region_name: this.newRegion.region_name,
            region_code: this.newRegion.region_code,
            proxy_type: this.newRegion.proxy_type,
            multiplier: parseFloat(this.newRegion.multiplier) || 1,
            active: true,
          },
        },
      });
      this.regions = [...this.regions, result.region];
      this.newRegion = { region_name: '', region_code: '', proxy_type: 'residential', multiplier: 1 };
      this.showNewRegionForm = false;
      this.message = { text: 'Region created.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to create region.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  // ── Provider Pricing Overrides ──────────────────────────────────────────
  // This entire resource had zero UI before — the backend CRUD
  // (Admin::ProxyviewsDirectory::CalculatorController#index_provider_pricing
  // etc.) existed but nothing ever called it.

  @tracked showNewProviderPricingForm = false;
  @tracked newProviderPricing = { provider_id: '', proxy_type: 'residential', price_per_gb: 0, region_multiplier: 1 };

  // Bulk price override form — applies one price/GB (and optionally one region
  // multiplier) to every provider pricing row in a single PUT to the bulk
  // endpoint. `bulkMultiplierValue` left blank means "don't touch multiplier".
  @tracked showBulkPriceForm = false;
  @tracked bulkPriceValue = '';
  @tracked bulkMultiplierValue = '';

  @action
  toggleBulkPriceForm() {
    this.showBulkPriceForm = !this.showBulkPriceForm;
  }

  @action
  updateBulkPriceField(field, event) {
    if (field === 'price') {
      this.bulkPriceValue = event.target.value;
    } else if (field === 'multiplier') {
      this.bulkMultiplierValue = event.target.value;
    }
  }

  @action
  async bulkApplyPrice() {
    // Validate that at least one field is filled — previously sent NaN
    // when the price field was blank.
    if (!this.bulkPriceValue.trim() && !this.bulkMultiplierValue.trim()) {
      this.message = { text: 'Enter at least a price or multiplier to apply.', type: 'danger' };
      return;
    }

    this.savingId = 'bulk-pricing';
    this.message = null;
    try {
      const overrides = this.providerPricings.map((pp) => {
        const o = { id: pp.id };
        // Only include fields that are actually filled — parseFloat('')
        // produces NaN which serializes as the string "NaN".
        if (this.bulkPriceValue.trim()) {
          o.price_per_gb = parseFloat(this.bulkPriceValue);
        }
        if (this.bulkMultiplierValue.trim()) {
          o.region_multiplier = parseFloat(this.bulkMultiplierValue);
        }
        return o;
      });
      const result = await ajax('/admin/plugins/proxyviews-directory/calculator/provider-pricing-bulk', {
        type: 'PUT',
        data: { overrides },
      });
      // Surface partial-success errors from the backend instead of
      // always claiming all overrides succeeded.
      const errorCount = result.errors?.length || 0;
      const updatedCount = result.updated || overrides.length;
      if (errorCount > 0) {
        this.message = {
          text: `Updated ${updatedCount}, ${errorCount} had errors: ${result.errors.join('; ')}`,
          type: 'danger',
        };
      } else {
        this.message = { text: `Bulk updated ${updatedCount} overrides.`, type: 'success' };
      }
      this.clearMessageAfterDelay();
      // Reload all provider pricings so the table reflects the new values.
      const fresh = await ajax('/admin/plugins/proxyviews-directory/calculator/provider-pricing');
      this.providerPricings = fresh || [];
      // Clear the form.
      this.bulkPriceValue = '';
      this.bulkMultiplierValue = '';
      this.showBulkPriceForm = false;
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to apply bulk price override.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  toggleNewProviderPricingForm() {
    this.showNewProviderPricingForm = !this.showNewProviderPricingForm;
  }

  @action
  updateNewProviderPricingField(field, event) {
    this.newProviderPricing = { ...this.newProviderPricing, [field]: event.target.value };
  }

  @action
  updateProviderPricingField(pricing, field, event) {
    pricing[field] = event.target.value;
  }

  @action
  async createProviderPricing() {
    if (!this.newProviderPricing.provider_id) {
      this.message = { text: 'Pick a provider.', type: 'danger' };
      return;
    }
    this.savingId = 'new-provider-pricing';
    this.message = null;
    try {
      const result = await ajax('/admin/plugins/proxyviews-directory/calculator/provider-pricing', {
        type: 'POST',
        data: {
          provider_pricing: {
            provider_id: parseInt(this.newProviderPricing.provider_id, 10),
            proxy_type: this.newProviderPricing.proxy_type,
            price_per_gb: parseFloat(this.newProviderPricing.price_per_gb) || 0,
            region_multiplier: parseFloat(this.newProviderPricing.region_multiplier) || 1,
            active: true,
          },
        },
      });
      this.providerPricings = [...this.providerPricings, result.provider_pricing];
      this.newProviderPricing = { provider_id: '', proxy_type: 'residential', price_per_gb: 0, region_multiplier: 1 };
      this.showNewProviderPricingForm = false;
      this.message = { text: 'Provider pricing override created.', type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = {
        text: err.responseJSON?.errors?.join(', ') || 'Failed to create provider pricing override.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async saveProviderPricing(pricing) {
    this.savingId = 'provider-pricing-' + pricing.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/calculator/provider-pricing/${pricing.id}`, {
        type: 'PUT',
        data: {
          provider_pricing: {
            proxy_type: this.calcDraft.proxy_type ?? pricing.proxy_type,
            price_per_gb: parseFloat(this.calcDraft.price_per_gb) || 0,
            region_multiplier: parseFloat(this.calcDraft.region_multiplier) || 1,
            active: pricing.active,
          },
        },
      });
      this.providerPricings = this.providerPricings.map((pp) => (pp.id === pricing.id ? { ...pp, ...this.calcDraft } : pp));
      this.editingCalcId = null;
      this.calcDraft = {};
      this.message = { text: `Saved override for ${pricing.provider_name}.`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save override.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  deleteProviderPricing(pricing) {
    this.dialog.yesNoConfirm({
      message: `Delete pricing override for ${pricing.provider_name}? This cannot be undone.`,
      didConfirm: async () => {
          this.savingId = 'delete-provider-pricing-' + pricing.id;
          this.message = null;
          try {
            await ajax(`/admin/plugins/proxyviews-directory/calculator/provider-pricing/${pricing.id}`, { type: 'DELETE' });
            this.providerPricings = this.providerPricings.filter((p) => p.id !== pricing.id);
            this.message = { text: `Deleted override for ${pricing.provider_name}.`, type: 'success' };
            this.clearMessageAfterDelay();
          } catch (err) {
            this.message = { text: 'Failed to delete override.', type: 'danger' };
          } finally {
            this.savingId = null;
          }
      }
    });
  }

  @action
  async saveSetting(setting) {
    this.savingId = 'setting-' + setting.id;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/calculator/settings/${setting.id}`, {
        type: 'PUT',
        data: {
          setting: {
            value: setting.value,
            description: setting.description
          }
        }
      });
      this.message = { text: `Saved setting "${setting.key}" successfully!`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save setting.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async saveSiteSetting(setting, previousValue) {
    this.savingId = 'site-setting-' + setting.key;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/site-settings/${setting.key}`, {
        type: 'PUT',
        data: {
          value: setting.value
        }
      });
      this.message = { text: `Saved platform setting "${setting.key}" successfully!`, type: 'success' };
      this.clearMessageAfterDelay();
    } catch (err) {
      // Roll the toggle back to its real (server) value so the checkbox never
      // shows a change that didn't actually persist. Reassigning the tracked
      // array forces the one-way `checked={{s.value}}` binding to re-render.
      if (arguments.length > 1) {
        setting.value = previousValue;
        this.siteSettings = [...this.siteSettings];
      }
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save site setting.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  toggleSiteSetting(setting, event) {
    const previousValue = setting.value;
    const newValue = event?.target?.checked !== undefined ? event.target.checked : !setting.value;
    set(setting, 'value', newValue);
    this.siteSettings = [...this.siteSettings];
    this.saveSiteSetting(setting, previousValue);
  }

  // For non-boolean site settings (string/integer/secret) — reads the input
  // value and sends it. The backend masks secrets on read, so the input
  // shows •••••••• when a key is set; the admin types a new value to replace.
  @action
  updateSiteSettingValue(setting, event) {
    setting.value = event.target.value;
    this.siteSettings = [...this.siteSettings];
  }

  @action
  async saveSiteSettingValue(setting) {
    this.savingId = 'site-setting-' + setting.key;
    this.message = null;
    try {
      await ajax(`/admin/plugins/proxyviews-directory/site-settings/${setting.key}`, {
        type: 'PUT',
        data: { value: setting.value },
      });
      this.message = { text: `Saved "${setting.key}".`, type: 'success' };
      this.clearMessageAfterDelay();
      // For secrets, re-mask after save by refetching just the settings list.
      if (setting.value_type === 'secret') {
        const fresh = await ajax('/admin/plugins/proxyviews-directory/site-settings');
        this.siteSettings = fresh || [];
      }
    } catch (err) {
      this.message = { text: err.responseJSON?.errors?.join(', ') || 'Failed to save setting.', type: 'danger' };
    } finally {
      this.savingId = null;
    }
  }

  @action
  updateRejectReason(claimId, event) {
    this.rejectReasons = {
      ...this.rejectReasons,
      [claimId]: event.target.value
    };
  }

  @tracked importResult = null;
  @tracked isDraggingBackup = false;
  @tracked pendingBackupFile = null;
  @tracked pendingBackupPreview = null;

  get pendingBackupSectionsList() {
    if (!this.pendingBackupPreview?.sections) {
      return [];
    }
    return Object.entries(this.pendingBackupPreview.sections).map(([key, count]) => ({ key, count }));
  }

  get importResultList() {
    if (!this.importResult?.imported) {
      return [];
    }
    return Object.entries(this.importResult.imported).map(([key, count]) => ({ key, count }));
  }

  // ── Backup Import & Drag-and-Drop ─────────────────────────────────────
  @action
  handleBackupDragOver(event) {
    event.preventDefault();
    this.isDraggingBackup = true;
  }

  @action
  handleBackupDragLeave(event) {
    event.preventDefault();
    this.isDraggingBackup = false;
  }

  @action
  handleBackupDrop(event) {
    event.preventDefault();
    this.isDraggingBackup = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.inspectBackupFile(file);
    }
  }

  @action
  handleBackupFileSelect(event) {
    const file = event.target.files && event.target.files[0];
    if (file) {
      this.inspectBackupFile(file);
    }
    event.target.value = '';
  }

  @action
  cancelPendingBackup() {
    this.pendingBackupFile = null;
    this.pendingBackupPreview = null;
  }

  async inspectBackupFile(file) {
    if (!file.name.endsWith('.json') && file.type !== 'application/json') {
      this.message = { text: 'Invalid file type. Please upload a valid JSON backup file.', type: 'danger' };
      return;
    }

    try {
      const text = await file.text();
      const payload = JSON.parse(text);

      if (!payload.sections || typeof payload.sections !== 'object') {
        this.message = { text: 'Invalid backup file structure. Missing "sections" key.', type: 'danger' };
        return;
      }

      const sectionCounts = {};
      let totalRecords = 0;

      for (const [key, value] of Object.entries(payload.sections)) {
        const count = Array.isArray(value) ? value.length : (value ? 1 : 0);
        sectionCounts[key] = count;
        totalRecords += count;
      }

      this.pendingBackupFile = file;
      this.pendingBackupPreview = {
        fileName: file.name,
        fileSizeFormatted: (file.size / 1024).toFixed(1) + ' KB',
        formatVersion: payload.format_version || 1,
        exportedAt: payload.exported_at ? new Date(payload.exported_at).toLocaleString() : 'Unknown',
        sourceUrl: payload.source_url || 'Unknown',
        sections: sectionCounts,
        totalRecords,
      };
      this.importResult = null;
      this.message = null;
    } catch (err) {
      this.message = { text: `Failed to parse backup JSON: ${err.message}`, type: 'danger' };
    }
  }

  @action
  async confirmAndExecuteImport() {
    if (!this.pendingBackupFile) {
      return;
    }

    this.savingId = 'import';
    this.message = null;
    this.importResult = null;
    try {
      const formData = new FormData();
      formData.append('file', this.pendingBackupFile);
      const result = await ajax('/admin/plugins/proxyviews-directory/backup/import', {
        type: 'POST',
        data: formData,
        contentType: false,
        processData: false,
      });

      this.importResult = result;
      const skippedNote = result.skipped && result.skipped.length
        ? ` Skipped user-attributed sections: ${result.skipped.join(', ')}.`
        : '';
      this.message = {
        text: `🎉 Import complete! Restored content tables cleanly.${skippedNote}`,
        type: 'success',
      };
      this.clearMessageAfterDelay();

      const summary = await ajax('/admin/plugins/proxyviews-directory/backup/summary');
      this.backupSummary = summary.summary || {};
      this.pendingBackupFile = null;
      this.pendingBackupPreview = null;
    } catch (err) {
      this.message = {
        text: err.jqXHR?.responseJSON?.errors?.join(', ') ||
              err.responseJSON?.errors?.join(', ') ||
              'Failed to import backup.',
        type: 'danger',
      };
    } finally {
      this.savingId = null;
    }
  }

  @action
  async importBackup(event) {
    return this.handleBackupFileSelect(event);
  }

  clearMessageAfterDelay() {
    setTimeout(() => {
      this.message = null;
    }, 4000);
  }

  // ── Seeding ────────────────────────────────────────────────────────────
  // Both seed endpoints are idempotent (find_or_initialize_by + skip
  // persisted), so re-running is safe. We still confirm because a seed on a
  // populated install is surprising if unintended.

  @action
  seedProviders() {
    this.dialog.yesNoConfirm({
      message: 'Seed sample providers (BrightData, Oxylabs, etc.)? Existing providers are skipped — this only adds missing ones.',
      didConfirm: async () => {
          this.savingId = 'seed-providers';
          this.message = null;
          try {
            const result = await ajax('/admin/plugins/proxyviews-directory/seed', { type: 'POST' });
            this.message = { text: result.message || 'Providers seeded.', type: 'success' };
            this.clearMessageAfterDelay();
            this.providersPage = 1;
            await this.loadProviders();
          } catch (err) {
            this.message = {
              text: err.responseJSON?.errors?.join(', ') || 'Failed to seed providers.',
              type: 'danger',
            };
          } finally {
            this.savingId = null;
          }
      }
    });
  }

  @action
  seedCalculator() {
    this.dialog.yesNoConfirm({
      message: 'Seed calculator defaults (pricing tiers, regions, settings)? Existing rows are left in place.',
      didConfirm: async () => {
          this.savingId = 'seed-calculator';
          this.message = null;
          try {
            const result = await ajax('/admin/plugins/proxyviews-directory/calculator/seed', { type: 'POST' });
            this.message = { text: result.message || 'Calculator defaults seeded.', type: 'success' };
            this.clearMessageAfterDelay();
            // Reload all three calculator resources so the new rows appear.
            const [tiers, regions, settings] = await Promise.all([
              ajax('/admin/plugins/proxyviews-directory/calculator/pricing'),
              ajax('/admin/plugins/proxyviews-directory/calculator/regions'),
              ajax('/admin/plugins/proxyviews-directory/calculator/settings'),
            ]);
            this.pricingTiers = tiers || [];
            this.regions = regions || [];
            this.settings = settings || [];
          } catch (err) {
            this.message = {
              text: err.responseJSON?.errors?.join(', ') || 'Failed to seed calculator defaults.',
              type: 'danger',
            };
          } finally {
            this.savingId = null;
          }
      }
    });
  }
}
